/**
 * The background-music catalogue: what a creator may lay under a book or a capsule.
 *
 * This module is deliberately about *shape*, not about music. What it ships is
 * the vocabulary — an id, a title, a category, a length and a *source* — so the
 * licensed set the app ships can be extended or replaced without touching one
 * surface that reads it. `#/lib/backgroundMusicLibrary` holds the rows; the
 * studio sheet, the two studios and the player only ever speak in these terms.
 *
 * Three things are kept separate on purpose:
 *
 *  1. **The music is not the theme.** A theme is how the page is printed; the
 *     music is what plays under it. They are chosen in two tabs, stored in two
 *     fields, and changing one never touches the other.
 *  2. **A bed is a file in the app.** `MusicSource` is a `require()`d module under
 *     `assets/sounds/` — the app ships its music — so a bed never streams from a
 *     host, plays offline, and cannot rot into a dead link. The manifest's
 *     provenance URLs are the importer's record; no runtime code reads them.
 *  3. **Selection is not playback.** Nothing here plays anything; this file is
 *     pure data and pure functions, so the sheet can be tested without audio and
 *     the player can be tested without a catalogue.
 */
import {type MessageDescriptor} from '@lingui/core'
import {msg} from '@lingui/core/macro'

/**
 * The shelf labels a creator browses by.
 *
 * `all` is not a category a track can belong to — it is the absence of a filter,
 * which is why `MusicTrack['category']` excludes it. Keeping that in the type
 * means a row can never be filed under "All" by mistake.
 */
export type MusicCategoryId =
  | 'all'
  | 'calm'
  | 'focus'
  | 'ambient'
  | 'happy'
  | 'cinematic'
  | 'soft'
  | 'energetic'
  | 'lofi'

export const MUSIC_CATEGORIES: {
  id: MusicCategoryId
  label: MessageDescriptor
}[] = [
  {id: 'all', label: msg`All`},
  {id: 'calm', label: msg`Calm`},
  {id: 'focus', label: msg`Focus`},
  {id: 'ambient', label: msg`Ambient`},
  {id: 'happy', label: msg`Happy`},
  {id: 'cinematic', label: msg`Cinematic`},
  {id: 'soft', label: msg`Soft`},
  {id: 'energetic', label: msg`Energetic`},
  {id: 'lofi', label: msg`Lo-fi`},
]

/** A category a track is actually filed under. */
export type MusicCategory = Exclude<MusicCategoryId, 'all'>

/**
 * Where a track's audio lives: a `require()`d asset under the app's own
 * `assets/sounds/` — a module number Metro resolves to a URI.
 *
 * Deliberately not a URL. The app ships its music rather than streaming it, which
 * is why the generator refuses a manifest row that names one and why
 * `#/lib/musicPlayback` has no branch that could fetch a bed from a host.
 */
export type MusicSource = {kind: 'bundled'; module: number}

export type MusicTrack = {
  /**
   * Stable, and the only thing stored alongside a book.
   *
   * Persisting the id rather than the audio is what keeps a book small and lets a
   * track be replaced — re-encoded, re-hosted, re-licensed — without every book
   * that chose it having to be rewritten.
   */
  id: string
  title: string
  category: MusicCategory
  /** The real length of the audio. The row shows it; see the library's test. */
  durationMs: number
  source: MusicSource
}

/**
 * Find a track by id.
 *
 * The one lookup every surface goes through, and it answers `undefined` rather
 * than throwing: a book may name a track that a later build no longer ships, and
 * a book whose music has been withdrawn should read silently, not crash.
 */
export function findMusicTrack(
  tracks: readonly MusicTrack[],
  id: string | null | undefined,
): MusicTrack | undefined {
  if (!id || id === MUSIC_NONE) return undefined
  return tracks.find(track => track.id === id)
}

/**
 * Fold a title for searching.
 *
 * Titles are written with accents and punctuation — "Café at Dawn", "Lo-fi
 * Rain" — and a creator types "cafe" and "lofi". Folding case, accents and
 * separators is the difference between a search field that works and one that
 * looks broken for exactly the tracks whose titles are prettiest.
 */
export function foldMusicText(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
}

/**
 * The rows a creator sees, for a category and a search.
 *
 * Search is a filter *within* the category rather than instead of it, because
 * the two controls sit on the same sheet and a creator who has tapped "Calm"
 * does not expect typing to silently leave it.
 */
export function browseMusic(
  tracks: readonly MusicTrack[],
  options: {category?: MusicCategoryId; query?: string} = {},
): MusicTrack[] {
  const category = options.category ?? 'all'
  const needle = foldMusicText(options.query ?? '')
  return tracks.filter(track => {
    if (category !== 'all' && track.category !== category) return false
    if (!needle) return true
    return foldMusicText(track.title).includes(needle)
  })
}

/** How many rows a category holds, ignoring any search — for the chips. */
export function countInCategory(
  tracks: readonly MusicTrack[],
  category: MusicCategoryId,
): number {
  return browseMusic(tracks, {category}).length
}

/**
 * A length, as a creator reads it: `0:08`, `3:05`, `1:02:00`.
 *
 * Seconds are padded and minutes are not, which is how every player in the world
 * prints a track, and it keeps a column of durations from ragged-right.
 */
export function formatTrackDuration(durationMs: number): string {
  const total = Math.max(0, Math.round(durationMs / 1000))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = total % 60
  const mm = hours > 0 ? String(minutes).padStart(2, '0') : String(minutes)
  return `${hours > 0 ? `${hours}:` : ''}${mm}:${String(seconds).padStart(2, '0')}`
}

/**
 * What a book stores about its music.
 *
 * The three states are three different intentions and collapsing them is what
 * loses a creator's choice:
 *
 *  - `undefined` — nothing has ever been chosen, so the book keeps whatever its
 *    theme has always played. This is the state every existing book is in, and
 *    it is why shipping a music library did not silently silence them.
 *  - `null` — the creator looked at the library and chose *silence*. Music that
 *    is not there is a choice, and it has to survive being saved.
 *  - a track id — that track is the bed.
 */
export type BackgroundMusicChoice = string | null | undefined

/** Whether a book with this choice should have music under it at all. */
export function wantsBackgroundMusic(choice: BackgroundMusicChoice): boolean {
  return choice !== null
}

/**
 * The persisted spelling of deliberate silence.
 *
 * A draft record stores one string field (`backgroundMusicId`), and that field
 * has to distinguish "the creator chose silence" from "nobody has chosen yet" —
 * the first is a decision that must survive a save, the second is what lets an
 * older book keep playing its theme's bed. An absent field carries the second,
 * so the first needs a value, and this is it. Reserved: the catalogue test holds
 * every shipped track id away from it.
 */
export const MUSIC_NONE = 'none'

/** The choice, read back out of a record that may predate the catalogue. */
export function musicChoiceFromRecord(value: unknown): BackgroundMusicChoice {
  if (typeof value !== 'string') return undefined
  return value === MUSIC_NONE ? null : value
}

/** The choice, as a record stores it. `undefined` is written as no field at all. */
export function musicChoiceToRecord(
  choice: BackgroundMusicChoice,
): string | undefined {
  if (choice === undefined) return undefined
  return choice === null ? MUSIC_NONE : choice
}

/**
 * The music a player should start, given a choice and the library.
 *
 * Returns the chosen track, `null` for deliberate silence, and `undefined` for
 * "nothing was chosen" — the one case where the caller falls back to the theme's
 * own bed. Keeping the three cases distinct here means no play surface has to
 * guess, and the `null` case can never be mistaken for "no music configured".
 */
export function resolveBackgroundMusic(
  tracks: readonly MusicTrack[],
  choice: BackgroundMusicChoice,
): MusicTrack | null | undefined {
  if (choice === undefined) return undefined
  if (choice === null) return null
  return findMusicTrack(tracks, choice) ?? null
}
